import { ReporteChequeo, ReporteRow } from "../models/ReporteChequeo";
import { STANDARD_SHIFT_CUTOFF_MINUTES } from "./nightScheduleEligibility.ts";

function timeToMinutes(time: string): number {
  const [hours, minutes, seconds] = time.split(":").map(Number);
  return hours * 60 + minutes + (seconds || 0) / 60;
}

function checkDateTimeMs(r: ReporteChequeo): number {
  const d = String(r.assistanceCheck_checkDate || "").slice(0, 10);
  const t = r.assistanceCheck_checkTime || "00:00:00";
  return new Date(`${d}T${t}`).getTime();
}

function isAfterCutoff(time: string, cutoffMinutes: number): boolean {
  return timeToMinutes(time) >= cutoffMinutes;
}

function isBeforeCutoffNextDay(time: string, cutoffMinutes: number): boolean {
  return timeToMinutes(time) < cutoffMinutes;
}

function calendarDayKey(r: ReporteChequeo): string {
  return String(r.assistanceCheck_checkDate || "").slice(0, 10);
}

const MAX_SINGLE_DAY_SHIFT_MINUTES = 14 * 60;
/** Jornada larga mismo día calendario (p. ej. 06:43–21:02 ≈ 14.3 h). */
const MAX_EXTENDED_SAME_DAY_SHIFT_MINUTES = 18 * 60;
const MIN_DAY_SHIFT_ENTRY_MINUTES = 6 * 60;
const MAX_DAY_SHIFT_ENTRY_MINUTES = 11 * 60;
const MIN_DAY_SHIFT_EXIT_MINUTES = 12 * 60;
const MAX_DAY_SHIFT_EXIT_MINUTES = 21 * 60;
const MIN_NIGHT_ENTRY_MINUTES = 18 * 60;
const MATUTINO_MAX_ENTRY_MINUTES = 10 * 60;
const MATUTINO_MIN_EXIT_MINUTES = 11 * 60;
const MATUTINO_MAX_EXIT_MINUTES = 17 * 60;

/**
 * Entrada temprana + salida mediodía el mismo calendario (p. ej. 05:35–13:56).
 * No confundir con salida nocturna (~05:xx) + entrada vespertina (~14:xx).
 */
export function isSameCalendarDayMatutinoShift(
  sortedSameDay: ReporteChequeo[]
): boolean {
  if (sortedSameDay.length < 2) {
    return false;
  }
  const firstMin = timeToMinutes(sortedSameDay[0].assistanceCheck_checkTime);
  const lastMin = timeToMinutes(
    sortedSameDay[sortedSameDay.length - 1].assistanceCheck_checkTime
  );
  if (lastMin <= firstMin || lastMin - firstMin > MAX_SINGLE_DAY_SHIFT_MINUTES) {
    return false;
  }
  return (
    firstMin < MATUTINO_MAX_ENTRY_MINUTES &&
    lastMin >= MATUTINO_MIN_EXIT_MINUTES &&
    lastMin <= MATUTINO_MAX_EXIT_MINUTES
  );
}

/**
 * Entrada matutina + salida nocturna el mismo calendario (p. ej. 06:43–21:02).
 * Sin horario nocturno: no emparejar la salida con la madrugada del día siguiente.
 */
export function isSameCalendarDayExtendedDayShift(
  sortedSameDay: ReporteChequeo[]
): boolean {
  if (sortedSameDay.length < 2) {
    return false;
  }
  const firstMin = timeToMinutes(sortedSameDay[0].assistanceCheck_checkTime);
  const lastMin = timeToMinutes(
    sortedSameDay[sortedSameDay.length - 1].assistanceCheck_checkTime
  );
  if (lastMin <= firstMin) {
    return false;
  }
  const duration = lastMin - firstMin;
  if (duration > MAX_EXTENDED_SAME_DAY_SHIFT_MINUTES) {
    return false;
  }
  if (firstMin >= MIN_NIGHT_ENTRY_MINUTES || firstMin >= MIN_DAY_SHIFT_EXIT_MINUTES) {
    return false;
  }
  return lastMin >= MIN_DAY_SHIFT_EXIT_MINUTES;
}

/**
 * Turno diurno en un solo día calendario (p. ej. 08:06–18:16).
 * No confundir con nocturno (05:56 salida noche anterior + 21:27 entrada noche).
 */
export function isSameCalendarDayDayShiftOrdered(
  sortedSameDay: ReporteChequeo[],
  cutoffMinutes: number
): boolean {
  if (sortedSameDay.length < 2) {
    return false;
  }
  const firstMin = timeToMinutes(sortedSameDay[0].assistanceCheck_checkTime);
  const lastMin = timeToMinutes(
    sortedSameDay[sortedSameDay.length - 1].assistanceCheck_checkTime
  );
  if (lastMin <= firstMin || lastMin - firstMin > MAX_SINGLE_DAY_SHIFT_MINUTES) {
    return false;
  }

  const hasDaytimeEntry = sortedSameDay.some((r) => {
    const m = timeToMinutes(r.assistanceCheck_checkTime);
    return (
      (m >= MIN_DAY_SHIFT_ENTRY_MINUTES && m <= MAX_DAY_SHIFT_ENTRY_MINUTES) ||
      (m >= cutoffMinutes && m <= MAX_DAY_SHIFT_ENTRY_MINUTES)
    );
  });
  const hasDaytimeExit = sortedSameDay.some((r) => {
    const m = timeToMinutes(r.assistanceCheck_checkTime);
    return m >= MIN_DAY_SHIFT_EXIT_MINUTES && m <= MAX_DAY_SHIFT_EXIT_MINUTES;
  });

  if (!hasDaytimeEntry || !hasDaytimeExit) {
    // Vespertino: solo checadas de tarde el mismo día (p. ej. 16:17–16:24).
    if (
      !hasDaytimeEntry &&
      hasDaytimeExit &&
      sortedSameDay.every((r) => {
        const m = timeToMinutes(r.assistanceCheck_checkTime);
        return (
          m >= MIN_DAY_SHIFT_EXIT_MINUTES && m < MIN_NIGHT_ENTRY_MINUTES
        );
      })
    ) {
      return true;
    }
    return false;
  }

  if (sortedSameDay.some((r) => timeToMinutes(r.assistanceCheck_checkTime) >= MIN_NIGHT_ENTRY_MINUTES)) {
    const hasMorningBeforeCutoff = sortedSameDay.some(
      (r) => timeToMinutes(r.assistanceCheck_checkTime) < cutoffMinutes
    );
    if (hasMorningBeforeCutoff) {
      return false;
    }
  }

  return true;
}

function sameDayDayShiftRow(
  ordered: ReporteChequeo[],
  branchId: number,
  cutoffMinutes: number,
  nightScheduleEnabled = false
): ReporteRow[] | null {
  if (nightScheduleEnabled) {
    return null;
  }
  const dayKey = calendarDayKey(ordered[0]);
  const sameDay = ordered
    .filter((r) => calendarDayKey(r) === dayKey)
    .sort((a, b) =>
      a.assistanceCheck_checkTime.localeCompare(b.assistanceCheck_checkTime)
    );
  if (
    !isSameCalendarDayDayShiftOrdered(sameDay, cutoffMinutes) &&
    !isSameCalendarDayExtendedDayShift(sameDay)
  ) {
    return null;
  }
  const checkIn = sameDay[0];
  const checkOut = sameDay[sameDay.length - 1];
  return [
    {
      branch_id: branchId,
      checkIn,
      checkOut:
        checkOut &&
        checkOut.assistanceCheck_id !== checkIn.assistanceCheck_id &&
        checkOut.assistanceCheck_checkTime !== checkIn.assistanceCheck_checkTime
          ? checkOut
          : null,
    },
  ];
}

/**
 * Marca 00:00:00 el mismo día calendario que una checada post-corte: al listar **solo el día
 * calendario actual** (segmentCurrent), evita que cuente como "entrada" y rompa primera–última
 * con el turno vespertino. No se aplica a marcas del día siguiente antes del corte
 * (segmentNextEarly): ahí el 00:00 puede ser salida válida de la noche anterior.
 */
function isMidnightBridgeArtifact(
  r: ReporteChequeo,
  peersSameCalendarList: ReporteChequeo[],
  cutoffMinutes: number
): boolean {
  const t = (r.assistanceCheck_checkTime || "").trim();
  if (t !== "00:00:00") {
    return false;
  }
  const day = calendarDayKey(r);
  return peersSameCalendarList.some((x) => {
    if (calendarDayKey(x) !== day) {
      return false;
    }
    if (x.assistanceCheck_id === r.assistanceCheck_id) {
      return false;
    }
    return isAfterCutoff(x.assistanceCheck_checkTime, cutoffMinutes);
  });
}

/** Con horario nocturno: excluye 00:00:00 manual/artefacto si el mismo día ya tiene tarde/noche. */
function isNightSchedulePairingArtifact(
  r: ReporteChequeo,
  peers: ReporteChequeo[],
  cutoffMinutes: number
): boolean {
  const t = (r.assistanceCheck_checkTime || "").trim();
  if (t !== "00:00:00") {
    return false;
  }
  const day = calendarDayKey(r);
  const minEveningMinutes = Math.max(cutoffMinutes, 18 * 60);
  return peers.some((x) => {
    if (calendarDayKey(x) !== day) {
      return false;
    }
    if (x.assistanceCheck_id === r.assistanceCheck_id) {
      return false;
    }
    return timeToMinutes(x.assistanceCheck_checkTime) >= minEveningMinutes;
  });
}

function filterNightPairingArtifacts(
  checks: ReporteChequeo[],
  cutoffMinutes: number,
  nightScheduleEnabled: boolean
): ReporteChequeo[] {
  if (!nightScheduleEnabled || checks.length === 0) {
    return checks;
  }
  const pool = checks;
  return checks.filter(
    (r) => !isNightSchedulePairingArtifact(r, pool, cutoffMinutes)
  );
}

/** Si el mayor hueco entre marcas consecutivas es menor o igual, se asume un solo tramo (reintentos), no dos visitas. */
const MAX_GAP_MINUTES_FOR_SINGLE_VISIT_BURST = 15;

function maxConsecutiveGapMinutes(ordered: ReporteChequeo[]): number {
  let max = 0;
  for (let i = 0; i < ordered.length - 1; i++) {
    const a = timeToMinutes(ordered[i].assistanceCheck_checkTime);
    const b = timeToMinutes(ordered[i + 1].assistanceCheck_checkTime);
    if (b >= a) {
      max = Math.max(max, b - a);
    }
  }
  return max;
}

/**
 * Sin horario nocturno: si hay entrada vespertina (≥10:00), madrugadas antes de las 03:00
 * del mismo calendario son salida del turno anterior (checkNext del día previo).
 * Entradas matutinas reales (07:xx) se conservan.
 */
const MIN_DAYTIME_ENTRY_MINUTES = 10 * 60;
const EARLY_MORNING_ARTIFACT_MAX_MINUTES = 6 * 60;

function segmentCurrentForStandardDayShift(
  sortedCurrent: ReporteChequeo[]
): ReporteChequeo[] {
  const sorted = [...sortedCurrent].sort((a, b) =>
    a.assistanceCheck_checkTime.localeCompare(b.assistanceCheck_checkTime)
  );
  if (isSameCalendarDayMatutinoShift(sorted)) {
    return sorted;
  }

  if (isSameCalendarDayExtendedDayShift(sorted)) {
    return sorted;
  }

  const hasLaterDaytimeEntry = sortedCurrent.some((r) =>
    isAfterCutoff(r.assistanceCheck_checkTime, MIN_DAYTIME_ENTRY_MINUTES)
  );
  if (!hasLaterDaytimeEntry) {
    return sortedCurrent;
  }
  return sortedCurrent.filter((r) => {
    const minutes = timeToMinutes(r.assistanceCheck_checkTime);
    if (minutes < STANDARD_SHIFT_CUTOFF_MINUTES) {
      return false;
    }
    if (minutes >= MIN_DAYTIME_ENTRY_MINUTES) {
      return true;
    }
    return minutes >= EARLY_MORNING_ARTIFACT_MAX_MINUTES;
  });
}

function sortChecksByDateTime(checks: ReporteChequeo[]): ReporteChequeo[] {
  return [...checks].sort((a, b) => checkDateTimeMs(a) - checkDateTimeMs(b));
}

/** Evita fila fantasma cuando la misma hora existe en dos fechas calendario (salida duplicada en BD). */
function dedupeStandardShiftChecks(
  segmentCurrent: ReporteChequeo[],
  segmentNextEarly: ReporteChequeo[],
): ReporteChequeo[] {
  const combined = [...segmentCurrent, ...segmentNextEarly];
  const byId = new Map<number, ReporteChequeo>();
  const byTime = new Map<string, ReporteChequeo>();

  for (const check of combined) {
    const id = check.assistanceCheck_id;
    if (id != null && byId.has(id)) {
      continue;
    }
    if (id != null) {
      byId.set(id, check);
    }

    const timeKey = check.assistanceCheck_checkTime || "";
    const existing = byTime.get(timeKey);
    if (!existing) {
      byTime.set(timeKey, check);
      continue;
    }
    if (calendarDayKey(check) > calendarDayKey(existing)) {
      byTime.set(timeKey, check);
    }
  }

  return sortChecksByDateTime([...byTime.values()]);
}

/**
 * Turno vespertino estándar (sucursal sin horario nocturno): entrada ~15:00, salida ~00:xx del día siguiente.
 * No usa día operativo; ignora madrugadas del mismo calendario y toma salida del día siguiente antes del corte.
 */
function buildOrderedChecksStandardDayShift(
  checkCurrent: ReporteChequeo[],
  checkNext: ReporteChequeo[],
  cutoffMinutes: number = 3 * 60
): ReporteChequeo[] {
  const sortedCurrent = [...checkCurrent].sort((a, b) =>
    a.assistanceCheck_checkTime.localeCompare(b.assistanceCheck_checkTime)
  );
  const sortedNext = [...checkNext].sort((a, b) =>
    a.assistanceCheck_checkTime.localeCompare(b.assistanceCheck_checkTime)
  );

  const segmentCurrent = segmentCurrentForStandardDayShift(sortedCurrent);

  const segmentNextEarly = sortedNext.filter((r) =>
    isBeforeCutoffNextDay(r.assistanceCheck_checkTime, cutoffMinutes)
  );

  return dedupeStandardShiftChecks(segmentCurrent, segmentNextEarly);
}

/**
 * Checadas del día en la sucursal seguidas de checadas del día siguiente antes del corte.
 *
 * Si en el **día calendario actual** hay al menos una marca en o después del corte, se incluyen
 * **todas** las marcas de ese día (así una entrada matutina antes del corte no se pierde cuando
 * hay salida vespertina el mismo día, p. ej. 07:51 + 15:14 con corte 08:00).
 */
export function buildOrderedChecksForBranchDay(
  checkCurrent: ReporteChequeo[],
  checkNext: ReporteChequeo[],
  cutoffMinutes: number = 3 * 60,
  nightScheduleEnabled: boolean = true
): ReporteChequeo[] {
  if (!nightScheduleEnabled) {
    return buildOrderedChecksStandardDayShift(
      checkCurrent,
      checkNext,
      cutoffMinutes
    );
  }

  const sortedCurrent = [...checkCurrent].sort((a, b) =>
    a.assistanceCheck_checkTime.localeCompare(b.assistanceCheck_checkTime)
  );
  const sortedNext = [...checkNext].sort((a, b) =>
    a.assistanceCheck_checkTime.localeCompare(b.assistanceCheck_checkTime)
  );

  const hasPostCutoffOnCurrentDay = sortedCurrent.some((r) =>
    isAfterCutoff(r.assistanceCheck_checkTime, cutoffMinutes)
  );
  const segmentAfterCutoffOnly = sortedCurrent.filter((r) =>
    isAfterCutoff(r.assistanceCheck_checkTime, cutoffMinutes)
  );

  const segmentCurrent =
    hasPostCutoffOnCurrentDay && sortedCurrent.length > 0
      ? sortedCurrent
      : segmentAfterCutoffOnly.length > 0
        ? segmentAfterCutoffOnly
        : sortedCurrent;

  const segmentNextEarly = sortedNext.filter((r) =>
    isBeforeCutoffNextDay(r.assistanceCheck_checkTime, cutoffMinutes)
  );

  const segmentCurrentFiltered = filterNightPairingArtifacts(
    segmentCurrent.filter(
      (r) => !isMidnightBridgeArtifact(r, sortedCurrent, cutoffMinutes)
    ),
    cutoffMinutes,
    true
  );
  const segmentNextFiltered = filterNightPairingArtifacts(
    segmentNextEarly,
    cutoffMinutes,
    true
  );

  if (
    hasPostCutoffOnCurrentDay &&
    !nightScheduleEnabled &&
    (isSameCalendarDayDayShiftOrdered(segmentCurrentFiltered, cutoffMinutes) ||
      isSameCalendarDayExtendedDayShift(segmentCurrentFiltered))
  ) {
    return segmentCurrentFiltered;
  }

  const sameDayShiftComplete =
    isSameCalendarDayDayShiftOrdered(segmentCurrentFiltered, cutoffMinutes) ||
    isSameCalendarDayExtendedDayShift(segmentCurrentFiltered);

  const segmentNextFinal =
    sameDayShiftComplete
      ? []
      : segmentNextFiltered.length > 1
        ? [segmentNextFiltered[0]]
        : segmentNextFiltered;

  return [...segmentCurrentFiltered, ...segmentNextFinal];
}

/** Minutos desde medianoche de checadas del colaborador en otras sucursales (mismo día lógico). */
function collectOtherBranchMinutes(
  partnerId: number,
  branchId: number,
  allCurrent: ReporteChequeo[],
  allNext: ReporteChequeo[],
  cutoffMinutes: number = 3 * 60
): number[] {
  const out: number[] = [];
  for (const r of allCurrent) {
    if (
      r.partner_id === partnerId &&
      r.branch_id !== branchId &&
      isAfterCutoff(r.assistanceCheck_checkTime, cutoffMinutes)
    ) {
      out.push(timeToMinutes(r.assistanceCheck_checkTime));
    }
  }
  for (const r of allNext) {
    if (
      r.partner_id === partnerId &&
      r.branch_id !== branchId &&
      isBeforeCutoffNextDay(r.assistanceCheck_checkTime, cutoffMinutes)
    ) {
      out.push(timeToMinutes(r.assistanceCheck_checkTime));
    }
  }
  return out;
}

/**
 * Si entre dos checadas consecutivas en esta sucursal hay marca en otra sucursal,
 * son visitas distintas (p. ej. salida 17:11 y regreso 20:21 con Macroplaza entre medias).
 */
function splitOrderedIntoSessions(
  ordered: ReporteChequeo[],
  otherBranchMinutes: number[]
): ReporteChequeo[][] {
  if (ordered.length === 0) {
    return [];
  }
  const sessions: ReporteChequeo[][] = [];
  let start = 0;
  for (let i = 0; i < ordered.length - 1; i++) {
    const a = timeToMinutes(ordered[i].assistanceCheck_checkTime);
    const b = timeToMinutes(ordered[i + 1].assistanceCheck_checkTime);
    const hasInterveningOtherBranch =
      a < b &&
      otherBranchMinutes.some((t) => t > a && t < b);
    if (hasInterveningOtherBranch) {
      sessions.push(ordered.slice(start, i + 1));
      start = i + 1;
    }
  }
  sessions.push(ordered.slice(start));
  return sessions;
}

function legacyFirstLastRow(
  ordered: ReporteChequeo[],
  branchId: number,
  cutoffMinutes: number = 3 * 60,
  nightScheduleEnabled = false
): ReporteRow[] {
  const dayShiftRow = sameDayDayShiftRow(
    ordered,
    branchId,
    cutoffMinutes,
    nightScheduleEnabled
  );
  if (dayShiftRow) {
    return dayShiftRow;
  }

  const first = ordered[0];
  const last = ordered[ordered.length - 1];
  if (!first || !last) {
    return [{ branch_id: branchId, checkIn: first, checkOut: null }];
  }

  const firstDateKey = String(first.assistanceCheck_checkDate || "").slice(0, 10);
  const lastDateKey = String(last.assistanceCheck_checkDate || "").slice(0, 10);
  const lastMinutes = timeToMinutes(last.assistanceCheck_checkTime);
  const crossesCalendarWithMorningExit =
    firstDateKey !== lastDateKey && lastMinutes < cutoffMinutes;

  const dayKey = firstDateKey;
  const sameDay = ordered
    .filter((r) => calendarDayKey(r) === dayKey)
    .sort((a, b) =>
      a.assistanceCheck_checkTime.localeCompare(b.assistanceCheck_checkTime)
    );
  if (!nightScheduleEnabled && isSameCalendarDayExtendedDayShift(sameDay)) {
    const checkIn = sameDay[0];
    const checkOut = sameDay[sameDay.length - 1];
    return [
      {
        branch_id: branchId,
        checkIn,
        checkOut:
          checkOut &&
          checkOut.assistanceCheck_id !== checkIn.assistanceCheck_id &&
          checkOut.assistanceCheck_checkTime !== checkIn.assistanceCheck_checkTime
            ? checkOut
            : null,
      },
    ];
  }

  let checkIn = first;
  if (crossesCalendarWithMorningExit && ordered.length >= 2) {
    const lastMs = checkDateTimeMs(last);
    let best: ReporteChequeo = first;
    let bestMs = -Infinity;
    for (let i = 0; i < ordered.length - 1; i++) {
      const c = ordered[i];
      const ms = checkDateTimeMs(c);
      if (ms < lastMs && ms > bestMs) {
        best = c;
        bestMs = ms;
      }
    }
    checkIn = best;
  }

  if (
    !nightScheduleEnabled &&
    checkIn &&
    timeToMinutes(checkIn.assistanceCheck_checkTime) >= MIN_NIGHT_ENTRY_MINUTES &&
    crossesCalendarWithMorningExit
  ) {
    return [{ branch_id: branchId, checkIn, checkOut: null }];
  }

  const checkOut =
    last &&
    last.assistanceCheck_id !== checkIn.assistanceCheck_id &&
    last.assistanceCheck_checkTime !== checkIn.assistanceCheck_checkTime
      ? last
      : null;
  return [{ branch_id: branchId, checkIn, checkOut }];
}

function rowsForSingleSession(
  ordered: ReporteChequeo[],
  branchId: number,
  cutoffMinutes: number = 3 * 60,
  nightScheduleEnabled = false
): ReporteRow[] {
  if (ordered.length === 0) {
    return [];
  }
  if (ordered.length === 1) {
    return [
      { branch_id: branchId, checkIn: ordered[0], checkOut: null },
    ];
  }
  if (ordered.length >= 3 && ordered.length % 2 === 1) {
    return legacyFirstLastRow(
      ordered,
      branchId,
      cutoffMinutes,
      nightScheduleEnabled
    );
  }
  if (
    ordered.length >= 4 &&
    ordered.length % 2 === 0 &&
    maxConsecutiveGapMinutes(ordered) <= MAX_GAP_MINUTES_FOR_SINGLE_VISIT_BURST
  ) {
    return legacyFirstLastRow(
      ordered,
      branchId,
      cutoffMinutes,
      nightScheduleEnabled
    );
  }
  const rows: ReporteRow[] = [];
  for (let i = 0; i < ordered.length; i += 2) {
    const checkIn = ordered[i];
    const checkOut = ordered[i + 1];
    rows.push({
      branch_id: branchId,
      checkIn,
      checkOut:
        checkOut &&
        checkOut.assistanceCheck_id !== checkIn.assistanceCheck_id &&
        checkOut.assistanceCheck_checkTime !== checkIn.assistanceCheck_checkTime
          ? checkOut
          : null,
    });
  }
  return rows;
}

/**
 * Filas de reporte por sucursal y día. Parte en sesiones si hubo checadas en otras sucursales
 * entre dos marcas consecutivas en esta sucursal (evita unir salida con re-entrada).
 *
 * Por sesión: impar ≥ 3 → primera–última; par con ≥4 marcas y huecos ≤15 min → una fila primera–última;
 * resto par → pareo consecutivo; una marca → salida vacía.
 */
export function buildVisitRowsForPartnerBranch(
  checkCurrent: ReporteChequeo[],
  checkNext: ReporteChequeo[],
  branchId: number,
  partnerId: number,
  allChecksCurrent: ReporteChequeo[],
  allChecksNext: ReporteChequeo[],
  cutoffMinutes: number = 3 * 60,
  nightScheduleEnabled: boolean = true
): ReporteRow[] {
  const ordered = buildOrderedChecksForBranchDay(
    checkCurrent,
    checkNext,
    cutoffMinutes,
    nightScheduleEnabled
  );
  if (ordered.length === 0) {
    return [];
  }

  const otherMinutes = collectOtherBranchMinutes(
    partnerId,
    branchId,
    allChecksCurrent,
    allChecksNext,
    cutoffMinutes
  );
  const sessions = splitOrderedIntoSessions(ordered, otherMinutes);

  const out: ReporteRow[] = [];
  for (const session of sessions) {
    if (!nightScheduleEnabled) {
      if (session.length === 0) {
        continue;
      }
      if (session.length === 1) {
        out.push({ branch_id: branchId, checkIn: session[0], checkOut: null });
        continue;
      }
      out.push(
        ...legacyFirstLastRow(session, branchId, cutoffMinutes, false)
      );
      continue;
    }
    out.push(
      ...rowsForSingleSession(
        session,
        branchId,
        cutoffMinutes,
        nightScheduleEnabled
      )
    );
  }
  return out;
}
